import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WeeklyActivityPreviewPanel from './WeeklyActivityPreviewPanel';
import QuarterHourTimeInput from './QuarterHourTimeInput';
import WeeklyActivityImageUpload from './WeeklyActivityImageUpload';
import SuggestMultilineField from './SuggestMultilineField';
import EquipmentNeedMultiSelect from './EquipmentNeedMultiSelect';
import { activityToPreviewModel, buildEditFromOrganizerDetail, buildPendingPutBody } from '../lib/weeklyActivityFormUtils';
import {
  defaultVenueEndFromStart,
  clampActivityStartToVenue,
  activityStartOptionsInVenueWindow,
  activityEndOptionsAfterStart,
  defaultActivityEnd,
} from '../lib/activityVenueTimes';
import { toastMessage } from '../lib/toast';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' };
const textareaStyle = { ...inputStyle, minHeight: 72, resize: 'vertical' };
const btnStyle = { padding: '8px 14px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const btnPrimary = { ...btnStyle, background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' };
const btnDanger = { ...btnStyle, background: '#dc3545', color: '#fff', borderColor: '#dc3545' };

const PAYMENT_OPTIONS = [
  { value: 'cash', label: '現金（現場付款）' },
  { value: 'ecpay_credit', label: '信用卡' },
];

const PASS_STATUS_LABELS = { issued: '已發', skipped: '不發', no_member: '無會員卡' };

function formatPassAt(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function rowVal(r, ...keys) {
  for (const k of keys) {
    if (r[k] != null && r[k] !== '') return r[k];
  }
  return '';
}

function formatVenueLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  const venue = item.venue || item.VENUE || '';
  const code = String(item.code || item.CODE || '').trim();
  return code ? `[${code}] ${venue}` : venue;
}

function venueValue(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.venue || item.VENUE || '';
}

// 比較兩個值是否不同
function isDifferent(oldVal, newVal) {
  const normalize = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'string') return v.trim();
    return v;
  };
  const a = normalize(oldVal);
  const b = normalize(newVal);
  return a !== b;
}

// 計算所有變更的欄位
function getChangedFields(original, revision) {
  if (!original || !revision) return [];
  
  const changes = [];
  const fieldLabels = {
    name: '活動名稱',
    activity_date: '日期',
    activity_time: '活動開始時間',
    end_time: '活動結束時間',
    venue_start_time: '場地開始時間',
    venue_end_time: '場地結束時間',
    venue: '場地',
    organizer: '主辦人',
    detail: '活動詳情',
    activity_flow: '活動流程',
    consumption_type: '消費方式',
    registration_link: '報名連結',
  };
  
  for (const [key, label] of Object.entries(fieldLabels)) {
    const oldVal = original[key] || original[key.toUpperCase()];
    const newVal = revision[key];
    if (newVal !== undefined && isDifferent(oldVal, newVal)) {
      changes.push({ field: key, label, oldValue: oldVal, newValue: newVal });
    }
  }
  
  return changes;
}

export default function OrganizerReviewDrawer({
  open,
  calendarId,
  onClose,
  onDone,
  adminAuthHeaders,
  venueList = [],
  beverageSuggestions: beverageSuggestionsProp,
  handoverSuggestions: handoverSuggestionsProp,
  internalBriefSuggestions: internalBriefSuggestionsProp,
  equipmentSuggestions: equipmentSuggestionsProp,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState({});
  const [approvalNote, setApprovalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [beverageSuggestions, setBeverageSuggestions] = useState(beverageSuggestionsProp || []);
  const [handoverSuggestions, setHandoverSuggestions] = useState(handoverSuggestionsProp || []);
  const [internalBriefSuggestions, setInternalBriefSuggestions] = useState(internalBriefSuggestionsProp || []);
  const [equipmentSuggestions, setEquipmentSuggestions] = useState(equipmentSuggestionsProp || []);

  useEffect(() => {
    if (Array.isArray(beverageSuggestionsProp)) setBeverageSuggestions(beverageSuggestionsProp);
  }, [beverageSuggestionsProp]);

  useEffect(() => {
    if (Array.isArray(handoverSuggestionsProp)) setHandoverSuggestions(handoverSuggestionsProp);
  }, [handoverSuggestionsProp]);

  useEffect(() => {
    if (Array.isArray(internalBriefSuggestionsProp)) setInternalBriefSuggestions(internalBriefSuggestionsProp);
  }, [internalBriefSuggestionsProp]);

  useEffect(() => {
    if (Array.isArray(equipmentSuggestionsProp)) setEquipmentSuggestions(equipmentSuggestionsProp);
  }, [equipmentSuggestionsProp]);

  useEffect(() => {
    if (!open) return;
    const hasAny = (beverageSuggestionsProp && beverageSuggestionsProp.length)
      || (handoverSuggestionsProp && handoverSuggestionsProp.length)
      || (internalBriefSuggestionsProp && internalBriefSuggestionsProp.length)
      || (equipmentSuggestionsProp && equipmentSuggestionsProp.length);
    if (hasAny) return;
    (async () => {
      try {
        const res = await fetch('/api/weekly-activity/admin/field-suggestions');
        const data = await res.json();
        if (data?.success) {
          setBeverageSuggestions(data.data?.beverageNeeds || []);
          setHandoverSuggestions(data.data?.handoverNotes || []);
          setInternalBriefSuggestions(data.data?.internalBriefs || []);
          setEquipmentSuggestions(data.data?.equipmentNeeds || []);
        }
      } catch { /* ignore */ }
    })();
  }, [open, beverageSuggestionsProp, handoverSuggestionsProp, internalBriefSuggestionsProp, equipmentSuggestionsProp]);

  const onImagesUploaded = (urls) => {
    setEdit((p) => ({ ...p, images: [...(p.images || []), ...urls] }));
  };

  const onImageUploadError = (err) => {
    window.alert(err.message || '上傳失敗');
  };

  const loadDetail = useCallback(async () => {
    if (!calendarId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending/${calendarId}`, { headers: adminAuthHeaders() });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '載入失敗');
      const d = data.data || {};
      setDetail(d);
      setEdit(buildEditFromOrganizerDetail(d));
      setApprovalNote(String(d.ORGANIZER_APPROVAL_NOTE ?? d.organizerApprovalNote ?? '').trim());
    } catch (e) {
      toastMessage(e.message || '載入失敗', 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [calendarId, adminAuthHeaders, onClose]);

  useEffect(() => {
    if (open && calendarId) loadDetail();
    if (!open) {
      setDetail(null);
      setEdit({});
      setApprovalNote('');
    }
  }, [open, calendarId, loadDetail]);

  const hasProduct = !!(detail?.VB_PRODUCT_ID ?? detail?.vbProductId ?? detail?.PRODUCT_ID ?? detail?.productId);
  const productSlug = edit.slug || rowVal(detail || {}, 'PRODUCT_SLUG', 'productSlug');
  const npReady = !!(detail?.EFFECTIVE_NOTIFICATION_PROFILE_ID ?? detail?.effectiveNotificationProfileId);
  const npSource = rowVal(detail || {}, 'EFFECTIVE_NOTIFICATION_SOURCE', 'effectiveNotificationSource');
  const npName = rowVal(detail || {}, 'EFFECTIVE_NOTIFICATION_PROFILE_NAME', 'effectiveNotificationProfileName')
    || rowVal(detail || {}, 'PRODUCT_NOTIFICATION_PROFILE_NAME', 'productNotificationProfileName')
    || rowVal(detail || {}, 'ORG_DEFAULT_NOTIFICATION_PROFILE_NAME', 'orgDefaultNotificationProfileName');
  const npSourceLabel = npSource === 'product' ? '商品已綁定'
    : npSource === 'organizer' ? '主辦個別設定'
      : npSource === 'global' ? '全系統預設' : '';

  // 判斷是否有綁定場地商品
  const hasVenueProduct = !!(
    detail?.VENUE_PRODUCT_ID 
    ?? detail?.venueProductId 
    ?? edit?.venue_product_id
  );

  const hasVenueOrder = !!(detail?.VB_ORDER_ID ?? detail?.vbOrderId);

  // 先定義這些變數（在 useCallback 之前）
  const hasRevision = !!(detail?.HAS_PENDING_REVISION ?? detail?.hasPendingRevision);
  const isCancelled = String(detail?.STATUS ?? detail?.status ?? '').toLowerCase() === 'cancelled';
  const isRejected = String(detail?.STATUS ?? detail?.status ?? '').toLowerCase() === 'rejected'
    || (!isCancelled
      && !!(detail?.ORGANIZER_REJECT_REASON ?? detail?.organizerRejectReason)
      && !(detail?.ORGANIZER_SUBMITTED_AT ?? detail?.organizerSubmittedAt));
  const cancelNote = isCancelled
    ? String(detail?.ORGANIZER_REJECT_REASON ?? detail?.organizerRejectReason ?? '').trim()
    : '';
  const rejectNote = String(detail?.ORGANIZER_REJECT_REASON ?? detail?.organizerRejectReason ?? '').trim();
  const revisionRaw = useMemo(() => {
    if (!detail) return null;
    try {
      const raw = detail.PENDING_REVISION_JSON ?? detail.pendingRevisionJson;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  }, [detail]);

  // 判斷欄位是否被變更
  const isFieldChanged = useCallback((fieldName) => {
    if (!hasRevision || !revisionRaw?.activity) return false;
    const oldVal = detail?.[fieldName] || detail?.[fieldName.toUpperCase()];
    const newVal = revisionRaw.activity[fieldName];
    return newVal !== undefined && isDifferent(oldVal, newVal);
  }, [hasRevision, revisionRaw, detail]);

  // 在輸入欄位上套用樣式（高亮變更的欄位）
  const getFieldStyle = useCallback((fieldName) => ({
    ...inputStyle,
    marginBottom: 10,
    ...(isFieldChanged(fieldName) ? {
      borderColor: '#fbbf24',
      borderWidth: 2,
      background: '#fffbeb',
    } : {}),
  }), [isFieldChanged]);

  const previewActivity = useMemo(() => {
    const merged = {
      ...detail,
      ...edit,
      registrationSlug: productSlug,
      is_public: edit.is_public !== undefined ? edit.is_public : true,
      is_internal: edit.is_internal !== undefined ? edit.is_internal : true,
    };
    return activityToPreviewModel(merged);
  }, [detail, edit, productSlug]);

  const publishedPreview = useMemo(() => (detail ? activityToPreviewModel(detail) : {}), [detail]);

  const revisionPreview = useMemo(() => {
    if (!revisionRaw?.activity || !detail) return null;
    const a = revisionRaw.activity;
    return activityToPreviewModel({
      ...detail,
      name: a.name ?? detail.NAME,
      activity_date: a.activity_date,
      activity_time: a.activity_time,
      end_time: a.end_time,
      venue_start_time: a.venue_start_time,
      venue_end_time: a.venue_end_time,
      venue: a.venue,
      organizer: a.organizer,
      detail: a.detail,
      activity_flow: a.activity_flow,
      consumption_type: a.consumption_type,
      images: a.images ?? detail.images,
    });
  }, [revisionRaw, detail]);

  const [issueDayPassSaving, setIssueDayPassSaving] = useState(false);
  const issueDayPassRaw = detail?.ISSUE_DAY_PASS ?? detail?.issueDayPass;
  const issueDayPass = !(issueDayPassRaw === false || issueDayPassRaw === 0);
  const passGrantStatus = String(detail?.PASS_GRANT_STATUS ?? detail?.passGrantStatus ?? '').trim();
  const passGrantText = passGrantStatus
    ? `日券：${PASS_STATUS_LABELS[passGrantStatus] || passGrantStatus}${detail?.PASS_GRANT_AT ? `（${formatPassAt(detail.PASS_GRANT_AT)}${detail?.PASS_GRANT_ADMIN === 'auto' ? '，自動' : ''}）` : ''}`
    : '';

  const toggleIssueDayPass = async (on) => {
    setIssueDayPassSaving(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-activity/${calendarId}/issue-day-pass`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ issueDayPass: on }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      setDetail((d) => (d ? { ...d, ISSUE_DAY_PASS: on } : d));
    } catch (e) {
      toastMessage(e.message || '儲存失敗', 'error');
    } finally {
      setIssueDayPassSaving(false);
    }
  };

  const putBody = () => {
    const body = buildPendingPutBody(edit, { approval_note: approvalNote });
    
    // 如果沒有綁定商品，清空場地時間
    if (!hasVenueProduct) {
      body.venue_start_time = null;
      body.venue_end_time = null;
    }
    
    return body;
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending/${calendarId}`, {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify(putBody()),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      toastMessage('已儲存', 'success');
      await loadDetail();
    } catch (e) {
      toastMessage(e.message || '儲存失敗', 'error');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (hasProduct && !npReady) return toastMessage('尚未設定通知設定檔，請至「主辦帳號」設定全系統預設或個別通知檔', 'error');
    setSaving(true);
    try {
      const putRes = await fetch(`/api/vb/admin/organizer-pending/${calendarId}`, {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify(putBody()),
      });
      const putData = await putRes.json().catch(() => ({}));
      if (!putData?.ok) throw new Error(putData?.error || '儲存欄位失敗，尚未核准');
      const res = await fetch(`/api/vb/admin/organizer-pending/${calendarId}/approve`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          approval_note: approvalNote,
          is_public: !!edit.is_public,
          is_internal: !!edit.is_internal,
          occupies_full_venue: !!edit.occupies_full_venue,
          beverage_need: edit.beverage_need,
          handover_notes: edit.handover_notes,
          internal_brief: edit.internal_brief,
          equipment_need: edit.equipment_need,
        }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '核准失敗');
      toastMessage(hasRevision ? '已核准改版' : '已核准上架', 'success');
      onDone();
      onClose();
    } catch (e) {
      toastMessage(e.message || '核准失敗', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelActivity = async () => {
    if (!window.confirm('確定取消此活動？')) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending/${calendarId}/cancel`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ note: approvalNote || '管理員取消主辦活動' }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '取消失敗');
      toastMessage('已取消', 'success');
      onDone();
      onClose();
    } catch (e) {
      toastMessage(e.message || '取消失敗', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (urls) => onImagesUploaded(urls);

  if (!open) return null;

  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const regUrl = productSlug ? `${base}/venue/booking/p/${productSlug}/form` : null;
  const venueOptions = venueList.length
    ? venueList
    : [...new Set((detail ? [edit.venue] : []).filter(Boolean))];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100,
      display: 'flex', justifyContent: 'flex-end',
    }}
    >
      <div style={{
        width: 'min(1100px, 100vw)', height: '100%', background: '#fff', overflow: 'auto',
        overflowX: 'hidden', padding: 16, boxSizing: 'border-box',
        boxShadow: '-4px 0 24px rgba(0,0,0,.15)', minWidth: 0,
      }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>審核主辦送審</h2>
          <button type="button" style={btnStyle} onClick={onClose}>關閉</button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : detail ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: (typeof window !== 'undefined' && window.innerWidth < 768)
              ? 'minmax(0, 1fr)'
              : 'minmax(0, 1fr) minmax(220px, min(380px, 36vw))',
            gap: 20,
            alignItems: 'start',
            minWidth: 0,
            maxWidth: '100%',
          }}
          >
            <div style={{ minWidth: 0, maxWidth: '100%' }}>
              <p style={{ fontSize: 14, color: '#555', marginTop: 0 }}>
                主辦：{rowVal(detail, 'ORGANIZER_DISPLAY_NAME', 'organizerDisplayName')} ({rowVal(detail, 'ORGANIZER_LOGIN', 'organizerLogin')})
              </p>

              {isCancelled && (
                <div style={{
                  marginBottom: 16, padding: 12, background: '#f5f5f5', border: '1px solid #d9d9d9',
                  borderRadius: 8, fontSize: 14,
                }}
                >
                  <div style={{ fontWeight: 600 }}>狀態：已取消（紀錄保留）</div>
                  {cancelNote ? <div style={{ marginTop: 4 }}>取消備註：{cancelNote}</div> : null}
                  <div style={{ marginTop: 4, color: '#666' }}>已取消不可重新上架，請新建活動。</div>
                </div>
              )}
              {isRejected && !isCancelled && (
                <div style={{
                  marginBottom: 16, padding: 12, background: '#fff1f0', border: '1px solid #ffa39e',
                  borderRadius: 8, fontSize: 14,
                }}
                >
                  <div style={{ fontWeight: 600 }}>狀態：退回中（場地仍佔用）</div>
                  {rejectNote ? <div style={{ marginTop: 4 }}>退回原因：{rejectNote}</div> : null}
                  <div style={{ marginTop: 4, color: '#666' }}>可「取消活動」釋放場地，或等主辦修改後再送審。</div>
                </div>
              )}

              {hasRevision && revisionRaw?.activity && (
                <div style={{
                  marginBottom: 16,
                  padding: 12,
                  background: '#fffbeb',
                  border: '1px solid #fbbf24',
                  borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#92400e' }}>
                    📝 待審修改項目
                  </div>
                  {(() => {
                    const changes = getChangedFields(detail, revisionRaw.activity);
                    if (changes.length === 0) {
                      return <div style={{ fontSize: 13, color: '#78350f' }}>無欄位變更</div>;
                    }
                    return (
                      <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 13, color: '#78350f' }}>
                        {changes.map(({ label, oldValue, newValue }, idx) => (
                          <li key={idx} style={{ marginBottom: 4 }}>
                            <strong>{label}</strong>：
                            <span style={{ textDecoration: 'line-through', opacity: 0.6, marginLeft: 4 }}>
                              {oldValue || '(空)'}
                            </span>
                            <span style={{ marginLeft: 4, marginRight: 4 }}>→</span>
                            <span style={{ fontWeight: 600 }}>{newValue || '(空)'}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

              <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
                  {hasRevision ? '待審新版（可編輯）' : '活動內容（可編輯）'}
                </h3>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>活動名稱</label>
                <input style={getFieldStyle('name')} value={edit.name || ''} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>日期</label>
                <input type="date" style={getFieldStyle('activity_date')} value={edit.activity_date || ''} onChange={(e) => setEdit((p) => ({ ...p, activity_date: e.target.value }))} />
                
                {hasVenueProduct ? (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>場地時段（卡場）</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <QuarterHourTimeInput
                        value={edit.venue_start_time || ''}
                        onChange={(v) => setEdit((p) => {
                          const venueEnd = v
                            ? (p.venue_end_time || defaultVenueEndFromStart(v))
                            : '';
                          const activityTime = v && venueEnd
                            ? clampActivityStartToVenue(p.activity_time || v, v, venueEnd)
                            : (p.activity_time || '');
                          const keepActEnd = p.end_time
                            && p.end_time !== p.venue_end_time;
                          return {
                            ...p,
                            venue_start_time: v,
                            venue_end_time: venueEnd,
                            activity_time: activityTime,
                            end_time: keepActEnd
                              ? p.end_time
                              : defaultActivityEnd(venueEnd, activityTime),
                          };
                        })}
                      />
                    </div>
                    <span style={{ fontSize: 14, color: '#888' }}>~</span>
                    <div style={{ flex: 1 }}>
                      <QuarterHourTimeInput
                        value={edit.venue_end_time || ''}
                        onChange={(v) => setEdit((p) => {
                          const start = p.venue_start_time || p.activity_time || '';
                          const activityTime = start && v
                            ? clampActivityStartToVenue(p.activity_time || start, start, v)
                            : (p.activity_time || '');
                          const keepActEnd = p.end_time && p.end_time !== p.venue_end_time;
                          return {
                            ...p,
                            venue_end_time: v,
                            activity_time: activityTime,
                            end_time: keepActEnd
                              ? p.end_time
                              : defaultActivityEnd(v, activityTime),
                          };
                        })}
                        allowClear
                      />
                    </div>
                  </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 10, padding: 8, background: '#f0f9ff', borderRadius: 6, fontSize: 13, color: '#0369a1' }}>
                    此場地未綁定包場商品，活動不需預約場地時段
                  </div>
                )}
                
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>活動起迄（月曆）</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <QuarterHourTimeInput
                        value={edit.activity_time || ''}
                        onChange={(v) => setEdit((p) => {
                          const start = p.venue_start_time || '';
                          const end = p.venue_end_time || '';
                          const activityTime = start && end
                            ? clampActivityStartToVenue(v, start, end)
                            : v;
                          const keepActEnd = p.end_time && p.end_time !== p.venue_end_time;
                          return {
                            ...p,
                            activity_time: activityTime,
                            end_time: keepActEnd
                              ? p.end_time
                              : defaultActivityEnd(p.venue_end_time, activityTime),
                          };
                        })}
                        allowedValues={(() => {
                          const opts = activityStartOptionsInVenueWindow(
                            edit.venue_start_time,
                            edit.venue_end_time,
                          );
                          return opts.length ? opts : null;
                        })()}
                      />
                    </div>
                    <span style={{ fontSize: 14, color: '#888' }}>~</span>
                    <div style={{ flex: 1 }}>
                      <QuarterHourTimeInput
                        value={edit.end_time || ''}
                        onChange={(v) => setEdit((p) => ({ ...p, end_time: v }))}
                        allowedValues={(() => {
                          const opts = activityEndOptionsAfterStart(edit.activity_time);
                          return opts.length ? opts : null;
                        })()}
                      />
                    </div>
                  </div>
                </div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>場地</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  {(() => {
                    const cur = venueOptions.find((v) => venueValue(v) === edit.venue);
                    const color = cur?.color || cur?.COLOR || '';
                    return color ? (
                      <span style={{
                        display: 'inline-block', width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        background: color, border: '1px solid #ccc',
                      }}
                      />
                    ) : null;
                  })()}
                  <select
                    style={{
                      ...inputStyle,
                      marginBottom: 0,
                      flex: 1,
                      ...(isFieldChanged('venue') ? {
                        borderColor: '#fbbf24',
                        borderWidth: 2,
                        background: '#fffbeb',
                      } : {}),
                    }}
                    value={edit.venue || ''}
                    onChange={(e) => setEdit((p) => ({ ...p, venue: e.target.value }))}
                    disabled={hasVenueOrder}
                  >
                    <option value="">-- 選擇場地 --</option>
                    {edit.venue && !venueOptions.some((v) => venueValue(v) === edit.venue) && (
                      <option value={edit.venue}>{edit.venue}</option>
                    )}
                    {venueOptions.map((v) => {
                      const val = venueValue(v);
                      return <option key={val} value={val}>{formatVenueLabel(v)}</option>;
                    })}
                  </select>
                  {hasVenueOrder && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                      已綁定場地訂單，如需更換場地請取消活動後重新建立
                    </div>
                  )}
                </div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>主辦（月曆顯示）</label>
                <input style={getFieldStyle('organizer')} value={edit.organizer || ''} onChange={(e) => setEdit((p) => ({ ...p, organizer: e.target.value }))} />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>消費方式</label>
                <input style={getFieldStyle('consumption_type')} value={edit.consumption_type || ''} onChange={(e) => setEdit((p) => ({ ...p, consumption_type: e.target.value }))} />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>報名連結</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <input
                    type="text"
                    inputMode="url"
                    style={{
                      ...inputStyle,
                      marginBottom: 0,
                      flex: 1,
                      ...(isFieldChanged('registration_link') ? {
                        borderColor: '#fbbf24',
                        borderWidth: 2,
                        background: '#fffbeb',
                      } : {}),
                    }}
                    value={edit.registration_link || ''}
                    onChange={(e) => setEdit((p) => ({ ...p, registration_link: e.target.value }))}
                    placeholder="https://"
                  />
                  {edit.registration_link ? (
                    <button
                      type="button"
                      onClick={() => setEdit((p) => ({ ...p, registration_link: '' }))}
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}
                    >
                      清除
                    </button>
                  ) : null}
                </div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>活動介紹</label>
                <textarea 
                  style={{
                    ...textareaStyle,
                    marginBottom: 10,
                    ...(isFieldChanged('detail') ? {
                      borderColor: '#fbbf24',
                      borderWidth: 2,
                      background: '#fffbeb',
                    } : {}),
                  }} 
                  rows={4} 
                  value={edit.detail || ''} 
                  onChange={(e) => setEdit((p) => ({ ...p, detail: e.target.value }))} 
                />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>活動流程</label>
                <textarea 
                  style={{
                    ...textareaStyle,
                    marginBottom: 10,
                    ...(isFieldChanged('activity_flow') ? {
                      borderColor: '#fbbf24',
                      borderWidth: 2,
                      background: '#fffbeb',
                    } : {}),
                  }} 
                  rows={4} 
                  value={edit.activity_flow || ''} 
                  onChange={(e) => setEdit((p) => ({ ...p, activity_flow: e.target.value }))} 
                />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>圖片</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {(edit.images || []).map((url, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }} />
                      <button type="button" onClick={() => setEdit((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#dc3545', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 11, cursor: 'pointer' }}
                      >×</button>
                    </div>
                  ))}
                </div>
                <WeeklyActivityImageUpload
                  adminAuthHeaders={adminAuthHeaders}
                  onUploaded={handleImageUpload}
                  onError={onImageUploadError}
                  label="選擇圖片"
                />
              </section>

              {hasProduct && (
                <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>報名商品（可編輯）</h3>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>slug</label>
                  <input style={{ ...inputStyle, marginBottom: 10 }} value={edit.slug || ''} onChange={(e) => setEdit((p) => ({ ...p, slug: e.target.value }))} />
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>報名費</label>
                      <input type="number" style={inputStyle} value={edit.event_price || ''} onChange={(e) => setEdit((p) => ({ ...p, event_price: e.target.value }))} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>會員價</label>
                      <input type="number" style={inputStyle} value={edit.member_price || ''} onChange={(e) => setEdit((p) => ({ ...p, member_price: e.target.value }))} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>名額</label>
                      <input type="number" style={inputStyle} value={edit.event_capacity || ''} onChange={(e) => setEdit((p) => ({ ...p, event_capacity: e.target.value }))} />
                    </div>
                  </div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>付款方式</label>
                  <select style={{ ...inputStyle, marginBottom: 10 }} value={edit.payment_method || 'ecpay_credit'} onChange={(e) => setEdit((p) => ({ ...p, payment_method: e.target.value }))}>
                    {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>商品說明</label>
                  <textarea style={{ ...textareaStyle, marginBottom: 10 }} rows={2} value={edit.product_description || ''} onChange={(e) => setEdit((p) => ({ ...p, product_description: e.target.value }))} />
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>報名問題</label>
                  {(edit.questions || []).map((q, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        value={q.questionText || ''}
                        onChange={(e) => {
                          const copy = [...(edit.questions || [])];
                          copy[i] = { ...copy[i], questionText: e.target.value };
                          setEdit((p) => ({ ...p, questions: copy }));
                        }}
                        placeholder={`問題 ${i + 1}`}
                      />
                      <button type="button" style={btnStyle} onClick={() => setEdit((p) => ({ ...p, questions: p.questions.filter((_, j) => j !== i) }))}>刪</button>
                    </div>
                  ))}
                  <button type="button" style={btnStyle} onClick={() => setEdit((p) => ({ ...p, questions: [...(p.questions || []), { questionText: '', questionType: 'text', isRequired: true }] }))}>新增問題</button>
                  {regUrl && (
                    <p style={{ marginTop: 10, fontSize: 13 }}>
                      <a href={regUrl} target="_blank" rel="noopener noreferrer">核准後報名連結</a>
                    </p>
                  )}
                </section>
              )}

              <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>顯示設定</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8 }}>
                  <input type="checkbox" checked={!!edit.is_public} onChange={(e) => setEdit((p) => ({ ...p, is_public: e.target.checked }))} />
                  顯示在前台行事曆
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!edit.is_internal} onChange={(e) => setEdit((p) => ({ ...p, is_internal: e.target.checked }))} />
                  顯示在櫃台系統
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginTop: 8 }}>
                  <input type="checkbox" checked={issueDayPass} disabled={issueDayPassSaving} onChange={(e) => toggleIssueDayPass(e.target.checked)} />
                  活動結束發日券（1 張）
                </label>
                {passGrantText ? <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{passGrantText}</div> : null}
              </section>

              <section style={{ marginBottom: 20, padding: 14, border: '2px solid #2563eb', borderRadius: 8, background: '#eff6ff' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1d4ed8' }}>給齊文的資訊</h3>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>主辦填寫（審核可改）· 主辦看得到</div>
                <SuggestMultilineField
                  label="簡述活動內容"
                  value={edit.internal_brief || ''}
                  onChange={(v) => setEdit((p) => ({ ...p, internal_brief: v }))}
                  suggestions={internalBriefSuggestions}
                  inputStyle={inputStyle}
                />
                <EquipmentNeedMultiSelect
                  label="需求設備"
                  value={edit.equipment_need || ''}
                  onChange={(v) => setEdit((p) => ({ ...p, equipment_need: v }))}
                  inputStyle={inputStyle}
                />
              </section>

              <section style={{ marginBottom: 20, padding: 14, border: '2px solid #28a745', borderRadius: 8, background: '#f0faf3' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1b7a36' }}>給櫃台（僅後台）</h3>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>後台填寫 · 主辦看不到</div>
                <SuggestMultilineField
                  label="需準備飲品"
                  value={edit.beverage_need || ''}
                  onChange={(v) => setEdit((p) => ({ ...p, beverage_need: v }))}
                  suggestions={beverageSuggestions}
                  inputStyle={inputStyle}
                />
                <SuggestMultilineField
                  label="交接事項"
                  value={edit.handover_notes || ''}
                  onChange={(v) => setEdit((p) => ({ ...p, handover_notes: v }))}
                  suggestions={handoverSuggestions}
                  inputStyle={inputStyle}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!edit.occupies_full_venue} onChange={(e) => setEdit((p) => ({ ...p, occupies_full_venue: e.target.checked }))} />
                  佔用全場地
                </label>
              </section>

              {hasProduct && (
                <section style={{ marginBottom: 16, padding: 12, background: npReady ? '#f6ffed' : '#fff1f0', borderRadius: 8, border: `1px solid ${npReady ? '#b7eb8f' : '#ffa39e'}` }}>
                  <b>通知設定檔：</b>
                  {npReady ? `${npName || '已設定'}${npSourceLabel ? `（${npSourceLabel}）` : ''}` : '未設定'}
                </section>
              )}

              <section style={{ marginBottom: 16, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>核准備註（主辦可見）</h3>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>後台填寫 · 主辦看得到（儲存後即可保留；核准後主辦端顯示）</div>
                <textarea style={textareaStyle} value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} placeholder="選填" />
              </section>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!isCancelled && (
                  <button type="button" style={btnStyle} disabled={saving} onClick={saveFields}>儲存欄位</button>
                )}
                {!isCancelled && (
                  <button type="button" style={btnPrimary} disabled={saving} onClick={approve}>
                    確認核准
                  </button>
                )}
                {!isCancelled && (
                  <button type="button" style={btnDanger} disabled={saving} onClick={cancelActivity}>取消活動</button>
                )}
                {isCancelled && (
                  <span style={{ fontSize: 13, color: '#888', alignSelf: 'center' }}>請改以「新建活動」上架</span>
                )}
              </div>
            </div>

            <div style={{ minWidth: 0, maxWidth: '100%' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 15, color: '#666' }}>即時預覽</h3>
              {hasRevision && (
                <div style={{ marginBottom: 12, padding: 10, background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 8, fontSize: 13 }}>
                  改版待審：下方對照目前上架與待審新版
                </div>
              )}
              {!hasRevision && <WeeklyActivityPreviewPanel activity={previewActivity} sticky />}
              {hasRevision && revisionPreview && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: (typeof window !== 'undefined' && window.innerWidth < 768) ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
                  gap: 12,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>目前上架</div>
                    <WeeklyActivityPreviewPanel activity={publishedPreview} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#0958d9' }}>待審新版</div>
                    <WeeklyActivityPreviewPanel activity={revisionPreview} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
